import { Header, Footer } from '@/shared/components/layout';
import { useDocumentTitle } from '@/shared/hooks';
import { Hero24, Features7, ContentSection01 } from './Components';
import { ContentSection02, Statistic1 } from '@/features/HowItWorks/Components';
import { TryItFree } from '@/features/Pricing/Components/TryItFree';

const AgentsPage = () => {
  useDocumentTitle(
    'Real Estate Leads for Real Estate Teams Canada - BrokerMax'
  );
  return (
    <div className="page font--jakarta">
      <Header variant="light" />
      <main>
        <Hero24
          title="Stop wasting money on ads. Get qualified leads that actually convert."
          description="High-intent buyer, tenant, and listing leads from REALTOR.ca — delivered to your inbox daily. No cold calling. No expensive ad campaigns. Just motivated prospects ready to buy, rent, or sell."
          backgroundImage="/images/hero-24.jpg"
        />
        <div className="container mx-auto max-w-7xl py-12 px-4">
          <div className="flex flex-wrap gap-8 opacity-30 mb-5 justify-center">
            {[
              {
                src: '/images/realtor-canada.png',
                alt: 'REALTOR.ca'
              },
              { src: '/images/brokerbay.png', alt: 'BrokerBay' },
              { src: '/images/canada.jpg', alt: 'Canada' },
              {
                src: '/images/proptx.png',
                alt: 'PropTx',
                padding: '10px'
              },
              {
                src: '/images/crea.png',
                alt: 'CREA',
                padding: '0px'
              }
            ].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain"
                style={{ padding: logo.padding }}
              />
            ))}
          </div>
          {/* END BADGES */}
        </div>
        <ContentSection01
          sectionId="lnk-1"
          sectionIdText="Proven Results"
          title="Replace expensive ad spend with predictable lead flow"
          description="While other agents burn $2,500+/month on Google Ads with 3% conversion rates, BrokerMax clients get qualified leads at a fraction of the cost. One Ontario agent generated 584 leads from 177 listings in 6 months — even during the worst market in years."
          subtitle="Real numbers from real agents"
          items={[
            '91% of lease leads are ready to rent within 30 days — not tire-kickers',
            "83% of buyer leads want to purchase within 45 days — they've already requested showings"
          ]}
          image="/images/img-05.png"
          imageAlt="Real estate lead generation results"
          imageFirst={false}
        />
        <Features7
          title="Built for real estate professionals"
          description="Everything you need to scale your business without the overhead"
          features={[
            {
              title: 'Shared & exclusive options',
              description:
                'Shared leads go to up to two agents; exclusive leads to one — pick what fits your budget and speed',
              icon: 'flaticon-layers-1'
            },
            {
              title: 'High-Intent Only',
              description:
                "Every lead has requested a showing on REALTOR.ca — they're actively looking, not browsing",
              icon: 'flaticon-click-1'
            },
            {
              title: 'Works for Teams & Brokerages',
              description:
                'Scale from solo agent to team leader with consistent monthly lead volume',
              icon: 'flaticon-prioritize'
            },
            {
              title: 'Zero Setup Friction',
              description:
                'Leads delivered via email or your CRM — start receiving leads within 24-48 hours',
              icon: 'flaticon-analytics'
            }
          ]}
        />
        <ContentSection02
          image="/images/img-07.png"
          imageAlt="Real estate agent dashboard"
          textBoxes={[
            {
              title: 'For Solo Agents',
              description:
                'Stop trading time for money. Get consistent lead flow so you can focus on closing deals, not chasing prospects. One agent built a 3-person team in 6 months because BrokerMax delivered more leads than she could handle alone.'
            },
            {
              title: 'For Teams & Brokerages',
              description:
                "Scale without the risk. Predictable lead volume means you can confidently hire and grow. No more wondering if you'll have enough deals to keep agents busy — BrokerMax ensures consistent monthly opportunities."
            }
          ]}
        />
        <Statistic1
          statistics={[
            {
              value: '584',
              suffix: '',
              label: 'Buyer & lease leads generated from 177 listings'
            },
            {
              value: '91',
              suffix: '%',
              label: 'Ready to buy or rent within 45-days'
            },
            {
              value: '10',
              suffix: '-15%',
              label: 'Average close rate on delivered leads'
            }
          ]}
        />
        <ContentSection02
          image="/images/img-02.png"
          imageAlt="Lead management system"
          textBoxes={[
            {
              title: 'No More Cold Outreach',
              description:
                "Every lead has already shown intent by requesting a showing on REALTOR.ca. They're actively searching — you just need to respond first. No door knocking, no cold calling, no wasted time on unqualified prospects."
            },
            {
              title: 'Three Types of Leads',
              description:
                'Buyer leads (83% ready within 45 days), lease leads (91% ready within 30 days), and FSBO listing opportunities. All from our network of high-traffic real estate websites across Canada.'
            }
          ]}
        />
        <ContentSection01
          sectionId="lnk-3"
          sectionIdText="Scalable Growth"
          title="From solo agent to team leader — without the guesswork"
          description="Most agents can't scale because they can't predict lead flow. BrokerMax changes that. Get consistent monthly leads even during slow seasons, so you can hire with confidence and grow your business predictably."
          image="/images/img-05.png"
          imageAlt="Real estate team growth"
          imageFirst={true}
        />
        <TryItFree />
      </main>
      <Footer />
    </div>
  );
};

export default AgentsPage;
